import React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Unstable_Grid2';
import ListSubheader from '@mui/material/ListSubheader';
import Button from '@mui/material/Button';

const Descriptions: React.FC = () => {
  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: '#151A1F',
    ...theme.typography.body1,
    padding: theme.spacing(1),
    textAlign: 'left',
    color: "#f2f4f6",
    overflow: 'auto',
    height:'fit-content',
    width: '15vw',
    position: 'fixed', 
  }));


  return (
    <div>
          <Box sx={{ width: '100%'}}>
          <Grid container spacing={1}>
            <Grid xs={2}>
              <Item>
                  <List 
                  subheader={
                    <ListSubheader component="div" id="nested-list-subheader" sx={{ width: '100%', margin: 'auto', color: '#ffff', bgcolor: '#2A343D', fontWeight: 'bold', fontSize: 16, textAlign: 'center'}}>
                      Contents
                    </ListSubheader>
                  }
                  >
                    <Divider />
                    <ListItem>
                      <ListItemButton href='#probs'>
                        <ListItemText primary="Probability Table" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemButton href='#overview'>
                        <ListItemText primary="Overview" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemButton href='#col'>
                        <ListItemText primary="Colonial House" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemButton href="#sfh">
                        <ListItemText primary="Single Family Home (SFH)" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemButton href="#ctmp">
                        <ListItemText primary="Contemporary Home" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemButton href="#fp">
                        <ListItemText primary="French Provincial (FP)" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemButton href="#apt">
                        <ListItemText primary="Apartment Building" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemButton href="#shack">
                        <ListItemText primary="Shack" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemButton href="#store">
                        <ListItemText primary="Store" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                    
                  </List>
              </Item>
            </Grid>
            <Grid xs={10} sx={{paddingLeft: 6, paddingRight: 18}}>
              <h1 id='probs'>Probability Table</h1>
              <p style={{marginBottom: '3rem', fontSize: 16}}>All probabilities of different features can be found in <Button variant="contained" color='success' href='https://docs.google.com/spreadsheets/d/1i6HYELhzocdz6B1RZKCzcj8belfXXU1U/edit#gid=424069488'>THIS SPREADSHEET</Button></p>
              <Divider sx={{marginBottom: '5rem'}}/>
              <h1 id='overview'>Features</h1>
              <p style={{marginBottom: '3rem', fontSize: 16}}>
                Each item in the collection is made up of the following features:
                <ul>
                  <li>Time of Day - The weather and time of day in the background</li>
                  <li>Street / View - The location of the land plot and the associated view</li>
                  <li>Land - The condition of the ground on the land plot</li>
                  <li>Path - The type of path leading up to the property</li>
                  <li>Property - The type of building on the land plot</li>
                </ul>
                Each of these features have a variety of possibilities, some much rarer than others. Properties themselves also have many different types which are explained below.    
              </p>
              <Divider sx={{marginBottom: '5rem'}}/>
              <h1>Property Types</h1>
                <p style={{marginBottom: '3rem', fontSize: 16}}>
                  There are currently 7 different property types, each of which have their own unique style and 
                  utility. In order of most common to least, the property types are: contemporary, colonial, 
                  single family home, French provincial, apartments, store and shack.   
                </p>   
                <Divider sx={{marginBottom: '5rem'}}/>
                <h2 id='col'>Colonial</h2>  
                <p style={{marginBottom: '3rem', fontSize: 16}}>
                  Colonial architecture dates back to the colonial era of the USA. These classic houses are two-storeyed symmetrical buildings which can have a primarily brick or weatherboard exterior. They are spacious and perfectly suited for growing families and maintain a modern look while showing off a classic architectural style. 
                  Colonial houses are comprised of:
                  <ul>
                    <li>Exterior Material</li>
                    <li>Door</li>
                    <li>Windows</li>
                    <li>Roof</li>
                    <li>Porch</li>
                    <li>Attic Windows</li>
                    <li>Chimney</li>
                </ul>
                </p>
                <Box
                  component="img"
                  sx={{
                    alignContent: 'left',
                    marginBottom: '3rem', 
                    maxHeight: '450px',
                    maxWidth: '400px'
                  }}
                  alt="Example Colonial"
                  src="https://i.imgur.com/n4a6M9k.png"
                />
                <Divider sx={{marginBottom: '5rem'}}/>
                <h2 id='sfh'>Single Family Home (SFH)</h2>  
                <p style={{marginBottom: '3rem', fontSize: 16}}>
                  The SFH is a staple of modern living. Providing for all the needs of a small family in a compact space, it is defined through its efficiency and comfort as a home. Not only is it the perfect place 
                  for a small family, but it can also serve as the perfect building base for extensions and further construction projects. It is a perfect foundation for 
                  the future. The SFH is made up of:   

                  <ul>
                    <li>Exterior Material</li>
                    <li>Door</li>
                    <li>Windows</li>
                    <li>Roof</li>
                    <li>Porch</li>
                </ul>
                </p>
                <Box
                  component="img"
                  sx={{
                    alignContent: 'left',
                    marginBottom: '3rem', 
                    maxHeight: '450px',
                    maxWidth: '400px'
                  }}
                  alt="Example SFH"
                  src="https://i.imgur.com/iBwzmaD.png"
                />
                <Divider sx={{marginBottom: '5rem'}}/>
                <h2 id='ctmp'>Contemporary Home</h2>  
                <p style={{marginBottom: '3rem', fontSize: 16}}>
                  Nothing says modern luxury more than contemporary architecture. Contemporary houses are asymmetric homes exhibiting the beauty of modern design all while 
                  maintaining the productivity of modern life. Whether it is your main house, or a vacation getaway, contemporary homes will be perfectly suited 
                  to all your needs. Contemporary homes consist of: 
                  <ul>
                    <li>Left Exterior Material</li>
                    <li>Right Exterior Material</li>
                    <li>Door</li>
                    <li>Left Exterior Design</li>
                    <li>Right Exterior Design</li>
                    <li>Roof</li>
                    <li>Lights</li>
                </ul>
                </p>
                <Box
                  component="img"
                  sx={{
                    alignContent: 'left',
                    marginBottom: '3rem', 
                    maxHeight: '450px',
                    maxWidth: '400px'
                  }}
                  alt="Example Contemporary"
                  src="https://i.imgur.com/3WizP3S.png"
                />
                <Divider sx={{marginBottom: '5rem'}}/>
                <h2 id='fp'>French Provincial (FP)</h2>  
                <p style={{marginBottom: '3rem', fontSize: 16}}>
                  The French Provincial house is a modernised version of the elegant architectural style stemming from the 
                  chateaus of the French countryside. These manors are ideal for those looking to enjoy the extravagance of life. Whether it is for day-to-day 
                  living or for holidays, this is where style meets luxury. These homes can be one of three colour sets, either dark, off-white or white. FP homes 
                  consist of:    
                  <ul>
                    <li>First Floor</li>
                    <li>Second Floor Centre</li>
                    <li>Second Floor Sides</li>
                    <li>Door</li>
                    <li>Entrance</li>
                    <li>Roof</li>
                    <li>Lights</li>
                </ul>
                </p>
                <Box
                  component="img"
                  sx={{
                    alignContent: 'left',
                    marginBottom: '3rem', 
                    maxHeight: '450px',
                    maxWidth: '400px'
                  }}
                  alt="Example FP"
                  src="https://i.imgur.com/oWc2Sbj.png"
                />
                <Divider sx={{marginBottom: '5rem'}}/>
                <h2 id='apt'>Apartment Building</h2>  
                <p style={{marginBottom: '3rem', fontSize: 16}}>
                  Low rise apartments which are superb for medium-density living. With 6 apartments per building, it is the ideal accommodation for single tenants
                  or small families looking to live in Altair while not worrying about the need to maintain a large property. The owners are able to lease the property to 
                  6 people. These buildings come in light and dark variants.     
                </p>
                <Box
                  component="img"
                  sx={{
                    alignContent: 'left',
                    marginBottom: '3rem', 
                    maxHeight: '450px',
                    maxWidth: '400px'
                  }}
                  alt="Example Apt"
                  src="https://i.imgur.com/HN3LgrK.png"
                />
                <Divider sx={{marginBottom: '5rem'}}/>
                <h2 id='shack' style={{marginBottom: '3rem'}}>Shack</h2>  
                <p style={{marginBottom: '3rem', fontSize: 16}}>
                  A simple wooden shack with a single room. Unlucky. However, it is the perfect blank canvas. With this property, you have the opportunity to turn rocks into diamonds. It is 
                  up to you, your vision, and your ambition. Shacks come in 3 wood variants; elm, pine and birch. 
                </p>
                <Box
                  component="img"
                  sx={{
                    alignContent: 'left',
                    marginBottom: '3rem', 
                    maxHeight: '450px',
                    maxWidth: '400px'
                  }}
                  alt="Example Shack"
                  src="https://i.imgur.com/2TjTC58.png"
                />
                <Divider sx={{marginBottom: '5rem'}}/>
                <h2 id='store'>Store</h2>  
                <p style={{fontSize: 16}}>
                  A small convenience store. A place of commerce where goods can be bought or sold, ideal for aspiring business owners looking to get a 
                  solid start in Altair. Stores come in 3 colour variants with distinct colour styles; neon, mint and orchid.       
                </p>
                <Box
                  component="img"
                  sx={{
                    alignContent: 'left',
                    marginBottom: '3rem', 
                    maxHeight: '450px',
                    maxWidth: '400px'
                  }}
                  alt="Example Store"
                  src="https://i.imgur.com/PGSgEF0.png"
                />
                <Divider sx={{marginBottom: '5rem'}}/>
            </Grid>
          </Grid>
          </Box>
  

           


    </div>
  );
};

export default Descriptions;